import React, { FC, useCallback, useEffect, useState } from 'react';
import { WppTabs, WppTab, WppButton, WppIconPlus } from '@wppopen/components-library-react';
import { TabsChangeEventDetail } from '@wppopen/components-library';
import { useNavigate, useParams } from 'react-router-dom';
import { ScreenshotWrapper, Footer, Header, PlotlyContainer, UpperLine } from 'components';
import styles from './Projection.module.scss';
import { Localization, normalizeString, routerFormat, TestIds } from 'utils';
import { useOs } from '@wppopen/react';
import { AnalyticsActionType } from '@wppopen/core';
import { useDispatch, useSelector } from 'react-redux';
import { projectionSlice, updateProjectionTab } from 'Redux/projectionsSlice';
import { TooltipCases } from './TooltipCases/TooltipCases';
import { OnBoardingSection } from './OnBoardingSection/OnBoardingSection';
import { RedirectButton } from 'components/RedirectButton/RedirectButton';

const {
    Main,
    MainTabs,
    MainTabsWrapper,
    MainTabsButton,
    MainContent,
    MainInfoSection,
    MainInfoWrapper,
} = styles;

interface IProjection {
    title: string;
    localization: Localization;
}

export const Projection: FC<IProjection> = ({ title, localization }) => {
    const [currentRef, setCurrentRef] = useState<HTMLDivElement | null>(null);
    const navigate = useNavigate();
    const { tabId } = useParams();
    const { osApi } = useOs();
    const dispatch = useDispatch();
    const {
        [localization]: { tabs, currentTab },
    } = useSelector(projectionSlice);

    const handleTabChange = useCallback(
        (event: CustomEvent<TabsChangeEventDetail>) => {
            dispatch(updateProjectionTab({ localization, tab: event.detail.value }));
            navigate(
                `/${localization}/projections/${routerFormat(event.detail.value as string, true)}`,
            );
        },
        [dispatch, localization, navigate],
    );

    useEffect(() => {
        osApi.analytics.track({
            type: AnalyticsActionType.page,
            payload: 'Segment Opportunity Simulator',
        });
    });

    useEffect(() => {
        if (tabId) {
            dispatch(
                updateProjectionTab({ localization, tab: routerFormat(tabId as string, false) }),
            );
        }
    }, [tabId, dispatch, localization]);

    return (
        <div className={Main} ref={(node) => setCurrentRef(node)}>
            <ScreenshotWrapper>
                <div className={MainInfoWrapper} id="title">
                    <div className={MainInfoSection}>
                        <Header title={title} localization={localization} isBackButtonShown={true}>
                            <RedirectButton />
                            <WppButton
                                onClick={() => navigate(`/${localization}/create_scenario`)}
                                id="createScenarioButton"
                                disabled={localization === Localization.UK}
                                data-testid={TestIds.projection.actions.createScenario}
                            >
                                <WppIconPlus color="white" slot="icon-start" />
                                <UpperLine title="Create Scenario" upperText="Beta" />
                            </WppButton>
                        </Header>
                    </div>
                    <div className={MainTabsWrapper}>
                        <WppTabs
                            value={currentTab.title}
                            onWppChange={handleTabChange}
                            className={MainTabs}
                            id="tabsSection"
                            data-testid={TestIds.projection.containers.tabContainer}
                        >
                            {Object.entries(tabs).map((tab: any) => {
                                return (
                                    <WppTab
                                        value={tab[0]}
                                        key={tab[0]}
                                        data-testid={`PROJECTION.MAIN.TAB.${normalizeString(
                                            tab[0],
                                        )}`}
                                    >
                                        <UpperLine title={tab[0]} key={tab[0]} />
                                    </WppTab>
                                );
                            })}
                        </WppTabs>
                        <div className={MainTabsButton}>
                            <OnBoardingSection />
                        </div>
                    </div>
                </div>
                <div className={MainContent}>
                    <PlotlyContainer
                        {...tabs[currentTab.title]}
                        refProp={currentRef}
                        localization={localization}
                        key={currentTab.title}
                        tooltipText={TooltipCases[localization][currentTab.title]}
                    />
                    <Footer localization={localization} />
                </div>
            </ScreenshotWrapper>
        </div>
    );
};
